"use client"

import { useEffect, useState } from "react"
import { IconPrinter } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getVisitPurposes } from "@/lib/clinical-visits-api"
import type { VisitPurposesMap } from "@/types"

/** The clinic's purposes, once each, in the order the suggestion map lists them. */
export function purposesInOrder(map: VisitPurposesMap): string[] {
  const seen: string[] = []
  for (const key of Object.keys(map).sort((a, b) => Number(a) - Number(b))) {
    for (const purpose of map[key] ?? []) {
      if (!seen.includes(purpose)) seen.push(purpose)
    }
  }
  return seen
}

/**
 * Radiance 261001 — the logistics desk's Print button: one printed travel
 * sheet per purpose the clinic runs (Prima Seduta, Seconda Seduta, Rpt). The
 * list is the clinic's own purpose map, never a hardcoded three, so a fourth
 * purpose added to the profile gets its sheet for free.
 *
 * Opens the print page in a new tab with the board's current range, so the
 * board stays where the desk left it.
 */
export function NeedsBookingPrintMenu({ days }: { days: number }) {
  const t = useTranslations("dashboard")
  const tv = useTranslations("visitCategorization")
  const [purposes, setPurposes] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    getVisitPurposes()
      .then((map) => {
        if (!cancelled) setPurposes(purposesInOrder(map))
      })
      .catch(() => {
        /* no purposes, no sheets — the button simply does not appear */
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (purposes.length === 0) return null

  const label = (key: string) => (tv.has(`purposes.${key}`) ? tv(`purposes.${key}`) : key)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          aria-label={t("frontdeskBooking.print.button")}
          title={t("frontdeskBooking.print.button")}
        >
          <IconPrinter className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("frontdeskBooking.print.menuTitle")}</DropdownMenuLabel>
        {purposes.map((purpose) => (
          <DropdownMenuItem
            key={purpose}
            onClick={() =>
              window.open(
                `/logistics/travel-sheet/print?purpose=${encodeURIComponent(purpose)}&days=${days}`,
                "_blank",
                "noopener",
              )
            }
          >
            {label(purpose)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
